import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  Tooltip,
  Chip,
  FormHelperText,
  Autocomplete,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import awsRegions from "aws-regions";
import { useDispatch, useSelector } from "react-redux";
import {
  postWafrAssessment,
  postWafrAssessmentPreValidate,
} from "../../../store/wafrQuestionnaire/wafrQuestionnaireSlice";
import { getUsername } from "../../../helpers/jwt-token-access/userRole";
import {
  getMockResponse,
  simulateApiDelay,
  MOCK_RESPONSE_ENABLED,
} from "./mockPreValidationResponse";
import InfoIcon from "@mui/icons-material/Info";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const CreateAssessmentModal = ({ open, onClose, accounts, users, orgCode }) => {
  const dispatch = useDispatch();
  const {
    isPostingWafrAssessment,
    isPostingWafrAssessmentPreValidate,
    postWafrAssessmentPreValidateMessage,
    postWafrAssessmentPreValidateError,
    postWafrAssessmentMessage,
  } = useSelector((state) => state.wafrQuestionnaire);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [isValidated, setIsValidated] = useState(false);
  const [preValidationData, setPreValidationData] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Error handling
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const loggedinUserEmail = getUsername()?.toLowerCase();
  // const loggedinUserEmailDomain = loggedinUserEmail?.split("@")[1];

  // const mspUsers = users
  //   ?.filter((user) => {
  //     if (user?.username) {
  //       const userDomain = user.username.toLowerCase().split("@")[1];
  //       return userDomain === loggedinUserEmailDomain;
  //     }
  //     return false;
  //   })
  //   .map((user) => ({
  //     ...user,
  //     username: user.username.toLowerCase(),
  //   }));

  // // Filter out MSP users from the full users list for Assessment Owner dropdown
  // const nonMspUsers = users?.filter((user) => {
  //   if (user?.username) {
  //     const userDomain = user.username.toLowerCase().split("@")[1];
  //     return userDomain !== loggedinUserEmailDomain;
  //   }
  //   return false;
  // });

  const [formData, setFormData] = useState({
    assessmentName: "",
    assessmentDescription: "",
    accountNumber: "",
    assessmentAwsRegions: [],
    primaryRegion: null,
    assessmentApprover: loggedinUserEmail || "",
    assessmentOwner: "",
  });

  const [errors, setErrors] = useState({});

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (!formData.assessmentApprover && loggedinUserEmail) {
      setFormData((prev) => ({
        ...prev,
        assessmentApprover: loggedinUserEmail,
      }));
    }
  }, [loggedinUserEmail]);

  // Reset modal state when it closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setIsValidated(false);
      setPreValidationData(null);
      setFormData({
        assessmentName: "",
        assessmentDescription: "",
        accountNumber: "",
        assessmentAwsRegions: [],
        primaryRegion: null,
        assessmentApprover: loggedinUserEmail || "",
        assessmentOwner: "",
      });
      setErrors({});
    }
  }, [open, loggedinUserEmail]);

  // Handle pre-validation success
  useEffect(() => {
    if (
      postWafrAssessmentPreValidateMessage &&
      !isPostingWafrAssessmentPreValidate
    ) {
      setIsValidated(true);
      setPreValidationData(postWafrAssessmentPreValidateMessage);
    }
  }, [
    postWafrAssessmentPreValidateMessage,
    isPostingWafrAssessmentPreValidate,
  ]);

  // Clear validation state when modal opens (in case of stale Redux state)
  useEffect(() => {
    if (open) {
      setIsValidated(false);
      setPreValidationData(null);
      setShowSuccessMessage(false);
    }
  }, [open]);

  // Handle successful assessment creation
  useEffect(() => {
    if (
      postWafrAssessmentMessage &&
      !isPostingWafrAssessment &&
      !showSuccessMessage
    ) {
      setShowSuccessMessage(true);
    }
  }, [postWafrAssessmentMessage, isPostingWafrAssessment, showSuccessMessage]);

  // Handle pre-validation error
  useEffect(() => {
    if (postWafrAssessmentPreValidateError) {
      setSnackbar({
        open: true,
        message: postWafrAssessmentPreValidateError,
        severity: "error",
      });
    }
  }, [postWafrAssessmentPreValidateError]);

  const validateField = (field, value, currentFormData) => {
    switch (field) {
      case "assessmentName":
        if (!value.trim()) return "Assessment name is required";
        if (value.trim().length < 3)
          return "Assessment name must be at least 3 characters";
        return null;

      case "assessmentDescription":
        if (!value.trim()) return "Description is required";
        if (value.trim().length < 3)
          return "Description must be at least 3 characters";
        return null;

      case "accountNumber":
        if (!value) return "Account number is required";
        return null;

      case "assessmentAwsRegions":
        if (value.length === 0) return "At least one region must be selected";
        if (!currentFormData.primaryRegion)
          return "Please select a primary region by clicking the star icon";
        return null;

      case "assessmentApprover":
        if (!value) return "Approver is required";
        return null;

      case "assessmentOwner":
        if (!value) return "Owner is required";
        return null;

      default:
        return null;
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Reset validation state when account or region changes
    if (field === "accountNumber" || field === "assessmentAwsRegions") {
      setIsValidated(false);
      setPreValidationData(null);
    }

    // Validate the field immediately
    const fieldError = validateField(field, value, formData);
    setErrors((prev) => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  const handlePrimaryRegionSelect = (regionCode, event) => {
    event.stopPropagation();

    setFormData((prev) => {
      const newData = {
        ...prev,
        primaryRegion: prev.primaryRegion === regionCode ? null : regionCode,
      };

      // Reset validation state when primary region changes
      setIsValidated(false);
      setPreValidationData(null);

      // Validate with the new state immediately
      const fieldError = validateField(
        "assessmentAwsRegions",
        newData.assessmentAwsRegions,
        newData
      );
      setErrors((prev) => ({
        ...prev,
        assessmentAwsRegions: fieldError,
      }));

      return newData;
    });
  };

  const handleRegionChange = (event) => {
    const value = event.target.value;

    setFormData((prev) => {
      const newData = {
        ...prev,
        assessmentAwsRegions: value,
        // Clear primary region if it's deselected
        primaryRegion: value.includes(prev.primaryRegion)
          ? prev.primaryRegion
          : null,
      };

      // Reset validation state when regions change
      setIsValidated(false);
      setPreValidationData(null);

      // Validate with the new state immediately
      const fieldError = validateField("assessmentAwsRegions", value, newData);
      setErrors((prev) => ({
        ...prev,
        assessmentAwsRegions: fieldError,
      }));

      return newData;
    });
  };

  const handlePreValidate = async () => {
    // Only validate account and region fields for step 1
    const step1Errors = {
      accountNumber: validateField(
        "accountNumber",
        formData.accountNumber,
        formData
      ),
      assessmentAwsRegions: validateField(
        "assessmentAwsRegions",
        formData.assessmentAwsRegions,
        formData
      ),
    };

    const hasStep1Errors = Object.values(step1Errors).some(
      (error) => error !== null
    );

    if (!hasStep1Errors) {
      // Check if mock response is enabled
      if (MOCK_RESPONSE_ENABLED) {
        console.log("🧪 Using mock pre-validation response");
        const mockResponse = getMockResponse();

        // Simulate API delay
        await simulateApiDelay();

        // Simulate successful response
        setIsValidated(true);
        setPreValidationData(mockResponse);
      } else {
        // Use real API
        const preValidatePayload = {
          accountNumber: formData.accountNumber,
          primaryRegion: formData.primaryRegion,
          secondaryRegions: formData.assessmentAwsRegions.filter(
            (region) => region !== formData.primaryRegion
          ),
        };
        dispatch(postWafrAssessmentPreValidate(preValidatePayload));
      }
    } else {
      setErrors((prev) => ({ ...prev, ...step1Errors }));
    }
  };

  const handleNext = () => {
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  // Helper function to check if any service in an object has failed
  const hasAnyFailedService = (services) => {
    if (!services) return false;
    return Object.values(services).some((service) => service === false);
  };

  // Helper function to determine automation type based on API response
  const getAutomationType = (data) => {
    if (!data) return "Standard";

    const { GlobalChecks, PrimaryRegionChecks, SecondaryRegionChecks } = data;

    // Check if any GlobalChecks fail
    if (hasAnyFailedService(GlobalChecks)) {
      return "CloudCatcher";
    }

    // Check if any PrimaryRegionChecks fail
    if (hasAnyFailedService(PrimaryRegionChecks)) {
      return "CloudCatcher";
    }

    // Check if any SecondaryRegionChecks fail
    if (SecondaryRegionChecks) {
      for (const region in SecondaryRegionChecks) {
        if (hasAnyFailedService(SecondaryRegionChecks[region])) {
          return "CloudCatcher";
        }
      }
    }

    // If all checks pass, it's Standard
    return "Standard";
  };

  // Helper function to determine if it's standard automation
  const isStandardAutomation = (data) => {
    return getAutomationType(data) === "Standard";
  };

  // Helper function to get info message
  const getInfoMessage = (data) => {
    if (!data) return "";

    const { CIS, GlobalChecks, PrimaryRegionChecks, SecondaryRegionChecks } =
      data;

    // Check if any service has failed across all regions
    const hasAnyFailedServiceInAnyRegion = () => {
      if (hasAnyFailedService(GlobalChecks)) return true;
      if (hasAnyFailedService(PrimaryRegionChecks)) return true;
      if (SecondaryRegionChecks) {
        for (const region in SecondaryRegionChecks) {
          if (hasAnyFailedService(SecondaryRegionChecks[region])) return true;
        }
      }
      return false;
    };

    // Case 3: CIS not available but all other checks are true
    if (CIS?.CISRunAvailable === false && !hasAnyFailedServiceInAnyRegion()) {
      return "No CIS Benchmark found. We have initiated CIS Benchmark Scan for you.";
    }

    // Case 4 & 5: CIS not present OR at least one Config is false in any region
    if (CIS?.CISRunAvailable === false || hasAnyFailedServiceInAnyRegion()) {
      return "Enable AWS Recommended Configurations to unlock Enhanced Automation. If not enabled, you can still proceed with CloudCatcher Automation.";
    }

    return "";
  };

  // Helper function to render check item with hyperlinks
  const renderCheckItem = (label, isPassed, url) => (
    <ListItem sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        {isPassed ? (
          <CheckCircleIcon sx={{ color: "success.main" }} />
        ) : (
          <CancelIcon sx={{ color: "error.main" }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          url ? (
            <Typography
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "14px",
                color: "primary.main",
                textDecoration: "underline !important",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.dark",
                  textDecoration: "underline !important",
                },
              }}
            >
              {label}
              <OpenInNewIcon 
                sx={{ 
                  fontSize: "10px", 
                  color: "primary.main",
                  opacity: 0.7,
                  verticalAlign: "super",
                  marginLeft: "2px"
                }} 
              />
            </Typography>
          ) : (
            <Typography
              sx={{
                fontSize: "14px",
                color: isPassed ? "text.primary" : "text.secondary",
              }}
            >
              {label}
            </Typography>
          )
        }
        primaryTypographyProps={url ? {} : {
          fontSize: "14px",
          color: isPassed ? "text.primary" : "text.secondary",
        }}
      />
    </ListItem>
  );

  // Helper function to check if verify button should be enabled
  const isVerifyButtonEnabled = () => {
    return (
      formData.accountNumber &&
      formData.assessmentAwsRegions.length > 0 &&
      formData.primaryRegion
    );
  };

  const handleCreateAssessment = () => {
    if (validateForm()) {
      const createAssessmentPayload = {
        orgCode,
        accountCode: accounts?.find(
          (account) => account.accountNumber === formData.accountNumber
        )?.accountCode,
        accountNumber: formData.accountNumber,
        workloadName: formData.assessmentName,
        description: formData.assessmentDescription,
        primaryRegion: formData.primaryRegion,
        regions: formData.assessmentAwsRegions,
        assessmentApprover: formData.assessmentApprover,
        assessmentOwner: formData.assessmentOwner,
        standardAutomation: isStandardAutomation(preValidationData),
      };
      dispatch(postWafrAssessment(createAssessmentPayload));
    }
  };

  const validateForm = () => {
    const newErrors = {
      assessmentName: validateField(
        "assessmentName",
        formData.assessmentName,
        formData
      ),
      assessmentDescription: validateField(
        "assessmentDescription",
        formData.assessmentDescription,
        formData
      ),
      accountNumber: validateField(
        "accountNumber",
        formData.accountNumber,
        formData
      ),
      assessmentAwsRegions: validateField(
        "assessmentAwsRegions",
        formData.assessmentAwsRegions,
        formData
      ),
      assessmentApprover: validateField(
        "assessmentApprover",
        formData.assessmentApprover,
        formData
      ),
      assessmentOwner: validateField(
        "assessmentOwner",
        formData.assessmentOwner,
        formData
      ),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== null);
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "relative",
          width: 500,
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
          border: "1px solid #e0e0e0",
          p: 0,
          marginRight: "480px", // Make space for the side panel
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#F5F5F5",
            padding: "16px 24px",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Create Assessment
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Form */}
        <Box
          component="form"
          sx={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Step 1: Account and Region Selection */}
          {currentStep === 1 && (
            <>
              <FormControl fullWidth error={!!errors.accountNumber}>
                <InputLabel required>Account Number</InputLabel>
                <Select
                  value={formData.accountNumber}
                  onChange={handleChange("accountNumber")}
                  label="Account Number *"
                >
                  {accounts?.map((account) => (
                    <MenuItem
                      key={account?.accountNumber}
                      value={account?.accountNumber}
                    >
                      {account?.accountNumber} - {account?.accountName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.accountNumber && (
                  <Typography color="error" variant="caption">
                    {errors.accountNumber}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!errors.assessmentAwsRegions}>
                <InputLabel required>AWS Region</InputLabel>
                <Select
                  multiple
                  value={formData.assessmentAwsRegions}
                  onChange={handleRegionChange}
                  label="AWS Region *"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((region) => (
                        <Chip
                          key={region}
                          label={region}
                          size="small"
                          sx={
                            formData.primaryRegion === region
                              ? {
                                  bgcolor: "#E3F2FD",
                                  borderColor: "#1976D2",
                                  "& .MuiChip-label": {
                                    color: "#1976D2",
                                    fontWeight: 500,
                                  },
                                }
                              : {}
                          }
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 224,
                        width: 250,
                      },
                    },
                  }}
                >
                  {awsRegions.list().map((region) => {
                    const isPrimary = formData.primaryRegion === region.code;
                    const isSelected = formData.assessmentAwsRegions.includes(
                      region.code
                    );

                    return (
                      <MenuItem
                        key={region.code}
                        value={region.code}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          ...(isPrimary && {
                            bgcolor: "#E3F2FD",
                          }),
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox checked={isSelected} />
                          <Typography variant="body2">{region.name} - {region.code}</Typography>
                        </Box>

                        {isSelected && (
                          <Tooltip
                            title={
                              isPrimary
                                ? "Remove as primary"
                                : "Set as primary region"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handlePrimaryRegionSelect(region.code, e)
                              }
                              sx={{
                                color: isPrimary ? "#1976D2" : "text.secondary",
                                "&:hover": {
                                  color: "#1976D2",
                                },
                              }}
                            >
                              {isPrimary ? (
                                <StarIcon fontSize="small" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "11px",
                    fontStyle: "italic",
                  }}
                >
                  Please activate Discovery for your AWS account from the AWS
                  Well-Architected Tool settings in your primary region. Select
                  regions and mark one as primary by clicking the star icon.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontSize: "11px",
                    fontStyle: "italic",
                    color: "primary.main",
                  }}
                >
                  Optional: Select all additional regions where the
                  application/workload resources are deployed.
                </Typography>
                <FormHelperText error={!!errors.assessmentAwsRegions}>
                  {errors.assessmentAwsRegions}
                </FormHelperText>
              </FormControl>

              {/* Pre-validation Results - Only show after successful verification */}
              {isValidated &&
                preValidationData &&
                !isPostingWafrAssessmentPreValidate &&
                (preValidationData.GlobalChecks ||
                  preValidationData.PrimaryRegionChecks ||
                  preValidationData.SecondaryRegionChecks ||
                  preValidationData.CIS ||
                  preValidationData.Summary?.actionItems) && (
                  <Card sx={{ mt: 2, border: "1px solid #E0E0E0" }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontSize: "16px", fontWeight: 600 }}
                      >
                        Service Validation Results
                      </Typography>

                      {/* CIS Check - At the top - Only show if CISRunAvailable is false */}
                      {preValidationData.CIS?.CISRunAvailable === false && (
                        <Box
                          sx={{
                            mb: 2,
                            pb: 2,
                            borderBottom: "1px solid #E0E0E0",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            CIS Benchmark
                          </Typography>
                          {preValidationData.CIS?.CISRunTriggered === true ? (
                            <Alert severity="info" sx={{ py: 1 }}>
                              No CIS Benchmark found. We have initiated CIS
                              Benchmark Scan for you.
                            </Alert>
                          ) : (
                            <Alert severity="warning" sx={{ py: 1 }}>
                              No CIS check available. Please contact support.
                            </Alert>
                          )}
                        </Box>
                      )}

                      <Box sx={{ display: "flex", gap: 2 }}>
                        {/* Global Checks */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            Global Services
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {renderCheckItem(
                              "AWS Business Support",
                              preValidationData.GlobalChecks?.BusinessSupport,
                              "https://docs.aws.amazon.com/awssupport/latest/user/changing-support-plans.html"
                            )}
                            {renderCheckItem(
                              "AWS Compute Optimizer",
                              preValidationData.GlobalChecks?.ComputeOptimizer,
                              "https://docs.aws.amazon.com/compute-optimizer/latest/ug/getting-started.html"
                            )}
                            {renderCheckItem(
                              "Cost Optimization Hub",
                              preValidationData.GlobalChecks
                                ?.CostOptimizationHub,
                              "https://docs.aws.amazon.com/cost-management/latest/userguide/coh-getting-started.html#coh-enable"
                            )}
                          </List>
                        </Box>

                        {/* Primary Region Checks */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            Regional Services
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {renderCheckItem(
                              "Security Hub",
                              preValidationData.PrimaryRegionChecks?.SecurityHub,
                              "https://docs.aws.amazon.com/securityhub/latest/userguide/securityhub-v2-enable.html#securityhub-v2-enable-standalone-account"
                            )}
                            {renderCheckItem(
                              "AWS Config",
                              preValidationData.PrimaryRegionChecks?.Config,
                              "https://docs.aws.amazon.com/config/latest/developerguide/gs-console.html#gs-console-setting-up.title"
                            )}
                            {renderCheckItem(
                              "AWS Discovery",
                              preValidationData.PrimaryRegionChecks
                                ?.WellArchitectedDiscovery,
                              `https://${formData.primaryRegion}.console.aws.amazon.com/wellarchitected/home?region=${formData.primaryRegion}#/settings/discovery`
                            )}
                          </List>
                        </Box>
                      </Box>

                      {/* Action Items - Grouped by Service */}
                      {preValidationData.Summary?.actionItems &&
                        preValidationData.Summary.actionItems.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid #E0E0E0",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                mb: 1,
                                fontWeight: 600,
                                color: "text.secondary",
                              }}
                            >
                              Action Items Required
                            </Typography>

                            {/* Group by service */}
                            {(() => {
                              const serviceGroups = {};
                              preValidationData.Summary.actionItems.forEach(
                                (item) => {
                                  if (!serviceGroups[item.service]) {
                                    serviceGroups[item.service] = {
                                      message: item.message,
                                      regions: [],
                                    };
                                  }
                                  // Only push region if it exists
                                  if (item.region) {
                                    serviceGroups[item.service].regions.push(
                                      item.region
                                    );
                                  }
                                }
                              );

                              return Object.entries(serviceGroups).map(
                                ([service, data]) => {
                                  // Map service names to display names and URLs
                                  const getServiceInfo = (serviceName) => {
                                    switch (serviceName) {
                                      case "ComputeOptimizer":
                                        return {
                                          displayName: "AWS Compute Optimizer",
                                          url: "https://docs.aws.amazon.com/compute-optimizer/latest/ug/getting-started.html"
                                        };
                                      case "SecurityHub":
                                        return {
                                          displayName: "Security Hub",
                                          url: "https://docs.aws.amazon.com/securityhub/latest/userguide/securityhub-v2-enable.html#securityhub-v2-enable-standalone-account"
                                        };
                                      case "Config":
                                        return {
                                          displayName: "AWS Config",
                                          url: "https://docs.aws.amazon.com/config/latest/developerguide/gs-console.html#gs-console-setting-up.title"
                                        };
                                      case "WellArchitectedDiscovery":
                                        return {
                                          displayName: "AWS Discovery",
                                          url: `https://${formData.primaryRegion}.console.aws.amazon.com/wellarchitected/home?region=${formData.primaryRegion}#/settings/discovery`
                                        };
                                      case "BusinessSupport":
                                        return {
                                          displayName: "AWS Business Support",
                                          url: "https://docs.aws.amazon.com/awssupport/latest/user/changing-support-plans.html"
                                        };
                                      case "CostOptimizationHub":
                                        return {
                                          displayName: "Cost Optimization Hub",
                                          url: "https://docs.aws.amazon.com/cost-management/latest/userguide/coh-getting-started.html#coh-enable"
                                        };
                                      default:
                                        return {
                                          displayName: serviceName,
                                          url: null
                                        };
                                    }
                                  };

                                  const serviceInfo = getServiceInfo(service);
                                  
                                  return (
                                    <Box key={service} sx={{ mb: 2 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mb: 1,
                                          fontWeight: 500,
                                          color: "error.main",
                                        }}
                                      >
                                        {serviceInfo.url ? (
                                          <Typography
                                            component="a"
                                            href={serviceInfo.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              color: "error.main",
                                              textDecoration: "underline",
                                              cursor: "pointer",
                                              "&:hover": {
                                                color: "error.dark",
                                              },
                                            }}
                                          >
                                            {serviceInfo.displayName}
                                            <OpenInNewIcon 
                                              sx={{ 
                                                fontSize: "10px", 
                                                color: "error.main",
                                                opacity: 0.7,
                                                verticalAlign: "super",
                                                marginLeft: "2px"
                                              }} 
                                            />
                                          </Typography>
                                        ) : (
                                          serviceInfo.displayName
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 1 }}
                                      >
                                        {data.message}
                                      </Typography>
                                      {data.regions && data.regions.length > 0 && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          Regions: {data.regions.join(", ")}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }
                              );
                            })()}
                          </Box>
                        )}

                      {/* Info Message */}
                      {getInfoMessage(preValidationData) && (
                        <Alert
                          severity="info"
                          sx={{ mt: 2 }}
                          icon={<InfoIcon />}
                        >
                          {getInfoMessage(preValidationData)}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}
            </>
          )}

          {/* Step 2: Assessment Details */}
          {currentStep === 2 && (
            <>
              {/* Pre-populated Account and Region (Read-only) */}
              <FormControl fullWidth>
                <InputLabel>Account Number</InputLabel>
                <Select
                  value={formData.accountNumber}
                  label="Account Number"
                  disabled
                >
                  <MenuItem value={formData.accountNumber}>
                    {formData.accountNumber} -{" "}
                    {
                      accounts?.find(
                        (acc) => acc.accountNumber === formData.accountNumber
                      )?.accountName
                    }
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>AWS Region</InputLabel>
                <Select
                  multiple
                  value={formData.assessmentAwsRegions}
                  label="AWS Region"
                  disabled
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((region) => (
                        <Chip
                          key={region}
                          label={region}
                          size="small"
                          sx={
                            formData.primaryRegion === region
                              ? {
                                  bgcolor: "#E3F2FD",
                                  borderColor: "#1976D2",
                                  "& .MuiChip-label": {
                                    color: "#1976D2",
                                    fontWeight: 500,
                                  },
                                }
                              : {}
                          }
                        />
                      ))}
                    </Box>
                  )}
                >
                  {formData.assessmentAwsRegions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}{" "}
                      {formData.primaryRegion === region && "(Primary)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 1 }} />

              <TextField
                label="Assessment Name"
                required
                value={formData.assessmentName}
                onChange={handleChange("assessmentName")}
                error={!!errors.assessmentName}
                helperText={errors.assessmentName}
              />

              <TextField
                label="Assessment Description"
                required
                multiline
                minRows={3}
                maxRows={5}
                value={formData.assessmentDescription}
                onChange={handleChange("assessmentDescription")}
                error={!!errors.assessmentDescription}
                helperText={errors.assessmentDescription}
              />

              <FormControl fullWidth error={!!errors.assessmentApprover}>
                <InputLabel required>Assessment Approver</InputLabel>
                <Select
                  value={formData.assessmentApprover}
                  onChange={handleChange("assessmentApprover")}
                  label="Assessment Approver *"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 224,
                      },
                    },
                  }}
                >
                  {users?.map((user) => (
                    <MenuItem key={user?.username} value={user?.username}>
                      {user?.username}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "11px",
                    fontStyle: "italic",
                  }}
                >
                  Person responsible for reviewing the WAF assessment and
                  ensuring compliance with WAFR requirements
                </Typography>
                {errors.assessmentApprover && (
                  <Typography color="error" variant="caption">
                    {errors.assessmentApprover}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!errors.assessmentOwner}>
                <Autocomplete
                  freeSolo
                  options={users?.map((user) => user?.username) || []}
                  value={formData.assessmentOwner}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      assessmentOwner: newValue || "",
                    }));

                    // Validate the field
                    const fieldError = validateField(
                      "assessmentOwner",
                      newValue || "",
                      formData
                    );
                    setErrors((prev) => ({
                      ...prev,
                      assessmentOwner: fieldError,
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      assessmentOwner: newInputValue,
                    }));

                    // Validate the field
                    const fieldError = validateField(
                      "assessmentOwner",
                      newInputValue,
                      formData
                    );
                    setErrors((prev) => ({
                      ...prev,
                      assessmentOwner: fieldError,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assessment Owner *"
                      error={!!errors.assessmentOwner}
                      helperText={errors.assessmentOwner}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  )}
                  noOptionsText="No users found"
                  placeholder="Select user or type custom identifier"
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "11px",
                    fontStyle: "italic",
                  }}
                >
                  Person who owns the workload/application and responsible to
                  remediate the findings.
                </Typography>
              </FormControl>

              {/* Success Message */}
              {showSuccessMessage && (
                <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Assessment Created
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ display: "inline", whiteSpace: "normal" }}>
                    As a next step, please close this dialog and initiate the automated check by clicking the{" "}
                    <Chip
                      icon={
                        <PlayArrowIcon 
                          sx={{ 
                            fontSize: "14px",
                            color: "#1976d2"
                          }}
                        />
                      }
                      label="Run"
                      size="small"
                      component="span"
                      sx={{
                        display: "inline-flex",
                        backgroundColor: "#ffffff",
                        color: "#1976d2",
                        border: "1px solid #e3f2fd",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        height: "32px",
                        borderRadius: "16px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        verticalAlign: "middle",
                        "& .MuiChip-icon": {
                          marginLeft: "8px",
                          marginRight: "4px",
                        },
                        "& .MuiChip-label": {
                          paddingLeft: "2px",
                          paddingRight: "12px",
                          fontWeight: 500,
                        },
                      }}
                    />
                    {" "}under "Run Automated checks"
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {/* Action Buttons */}
          <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
            {!showSuccessMessage && (
              <Box>
                {currentStep === 2 && (
                  <Button variant="outlined" onClick={handleBack}>
                    Back
                  </Button>
                )}
              </Box>
            )}
            <Box
              display="flex"
              gap={2}
              sx={{ ml: showSuccessMessage ? "auto" : 0 }}
            >
              {!showSuccessMessage && (
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
              )}
              {currentStep === 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    isPostingWafrAssessmentPreValidate ||
                    !isVerifyButtonEnabled()
                  }
                  onClick={isValidated ? handleNext : handlePreValidate}
                >
                  {isPostingWafrAssessmentPreValidate
                    ? "Verifying..."
                    : isValidated
                    ? isStandardAutomation(preValidationData)
                      ? "Next"
                      : `${getAutomationType(preValidationData)} Automation`
                    : "Verify"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isPostingWafrAssessment}
                  onClick={
                    showSuccessMessage ? onClose : handleCreateAssessment
                  }
                >
                  {isPostingWafrAssessment
                    ? "Saving..."
                    : showSuccessMessage
                    ? "Close"
                    : "Save"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateAssessmentModal;
